import { useEffect, useState } from "react";
import {
  getStatisticsCollections,
  getWasteStatistic,
} from "../../services/container";
import {
  getPreviousPeriod,
  hasComparablePreviousSortingHistory,
} from "./dashboardKpiUtils";

const EMPTY_KEYWORD_FILTER = JSON.stringify([]);

const emptyCollections = {
  analyzedVolume: 0,
  rotationCurrentCount: 0,
  rotationPreviousCount: 0,
  collectionDataResult: {},
  collectionPreviousDataResult: {},
  collectionsReady: false,
};

const emptySorting = {
  sortingCurrentCount: 0,
  sortingPreviousCount: 0,
  sortingDataResult: {},
  sortingReady: false,
  hasComparablePreviousSorting: false,
};

export const useDashboardPeriodStats = (
  periodStart,
  periodStop,
  loadSortingStats,
) => {
  const [collections, setCollections] = useState(emptyCollections);
  const [sorting, setSorting] = useState(emptySorting);

  useEffect(() => {
    if (!periodStart || !periodStop) return undefined;
    let cancelled = false;
    const previous = getPreviousPeriod(periodStart, periodStop);

    setCollections((current) => ({ ...current, collectionsReady: false }));
    Promise.all([
      getStatisticsCollections({
        periodStart,
        periodStop,
        keywordFilter: EMPTY_KEYWORD_FILTER,
      }),
      getStatisticsCollections({
        periodStart: previous.periodStart,
        periodStop: previous.periodStop,
        keywordFilter: EMPTY_KEYWORD_FILTER,
      }),
    ])
      .then(([current, previousResponse]) => {
        if (cancelled) return;
        const volume = Number(current.data?.totalVolumeMaterial);
        setCollections({
          analyzedVolume: Number.isFinite(volume) ? volume : 0,
          rotationCurrentCount: current.data?.countCollections || 0,
          rotationPreviousCount: previousResponse.data?.countCollections || 0,
          collectionDataResult: current.data?.dataResult || {},
          collectionPreviousDataResult: previousResponse.data?.dataResult || {},
          collectionsReady: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCollections({ ...emptyCollections, collectionsReady: true });
      });

    return () => {
      cancelled = true;
    };
  }, [periodStart, periodStop]);

  useEffect(() => {
    if (!loadSortingStats || !periodStart || !periodStop) {
      setSorting({ ...emptySorting, sortingReady: !loadSortingStats });
      return undefined;
    }
    let cancelled = false;
    const previous = getPreviousPeriod(periodStart, periodStop);

    setSorting((current) => ({ ...current, sortingReady: false }));
    Promise.all([
      getWasteStatistic({ periodStart, periodStop }),
      getWasteStatistic({
        periodStart: previous.periodStart,
        periodStop: previous.periodStop,
      }),
    ])
      .then(([current, previousResponse]) => {
        if (cancelled) return;
        const stats = current.data?.statisticsData || {};
        const previousStats = previousResponse.data?.statisticsData || {};
        const previousDataResult = previousStats.dataResult || {};
        setSorting({
          sortingCurrentCount: stats.countAnomalies || 0,
          sortingPreviousCount: previousStats.countAnomalies || 0,
          sortingDataResult: stats.dataResult || {},
          sortingReady: true,
          hasComparablePreviousSorting: hasComparablePreviousSortingHistory(
            previousDataResult,
            previous.periodStart,
          ),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSorting({ ...emptySorting, sortingReady: true });
      });

    return () => {
      cancelled = true;
    };
  }, [loadSortingStats, periodStart, periodStop]);

  return { ...collections, ...sorting };
};
